import chainHandlersAsync from "@gef-ui/utils/chainHandlersAsync";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from 'prop-types';
import React, { useId } from 'react';
import { useModalState } from '../../../hooks/useDialogState';
import { useDefaultComponentValue } from '../../utils';
import CrossButton from '../CrossButton';
import DraggablePaper from '../DraggablePaper';
const COMPONENT_NAME = 'GenericDialog';
const resolveContent = (Content, onExit)=>{
    if (Content instanceof String) {
        return /*#__PURE__*/ React.createElement(DialogContentText, null, "Content");
    } else if (Content instanceof Function) {
        return /*#__PURE__*/ React.createElement(Content, {
            exit: onExit
        });
    } else {
        return Content;
    }
};
const resolveActions = (Actions, onExit, DialogActionsProps)=>{
    if (Actions instanceof Function) {
        return /*#__PURE__*/ React.createElement(DialogActions, DialogActionsProps, /*#__PURE__*/ React.createElement(Actions, {
            exit: onExit
        }));
    } else if (Actions) {
        return /*#__PURE__*/ React.createElement(DialogActions, DialogActionsProps, Actions);
    } else {
        return null;
    }
};
const resolveFooter = (Footer, onExit)=>{
    if (Footer instanceof Function) {
        return /*#__PURE__*/ React.createElement(Footer, {
            exit: onExit
        });
    } else {
        return Footer;
    }
};
const GenericDialog = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { onExit, onCrossClick, onBackdropClick, title, withCross = false, backdropClose = true, actions, content, footer, DialogContentProps = {}, DialogActionsProps = {}, DialogTitleProps = {}, draggable, RootWrapper = React.Fragment, RootWrapperProps = {}, CrossButtonProps = {}, ...other } = props;
    const dialogTitleId = useId();
    return /*#__PURE__*/ React.createElement(Dialog, {
        onClose: backdropClose ? chainHandlersAsync([
            onBackdropClick,
            onExit
        ]) : null,
        PaperComponent: draggable ? DraggablePaper : null,
        slotProps: {
            paper: draggable ? {
                titleId: dialogTitleId
            } : undefined
        },
        ...other
    }, /*#__PURE__*/ React.createElement(RootWrapper, RootWrapperProps, (title || withCross) && /*#__PURE__*/ React.createElement(DialogTitle, {
        id: dialogTitleId,
        ...DialogTitleProps,
        bgstyle: title ? 'title' : 'no-title'
    }, title, withCross && /*#__PURE__*/ React.createElement(CrossButton, {
        onClick: chainHandlersAsync([
            onCrossClick,
            onExit
        ]),
        ...CrossButtonProps
    })), /*#__PURE__*/ React.createElement(DialogContent, DialogContentProps, resolveContent(content, onExit)), resolveActions(actions, onExit, DialogActionsProps), resolveFooter(footer, onExit)));
};
GenericDialog.propTypes = {
    CrossButtonProps: PropTypes.object,
    DialogActionsProps: PropTypes.object,
    DialogContentProps: PropTypes.object,
    DialogTitleProps: PropTypes.object,
    RootWrapper: PropTypes.elementType,
    RootWrapperProps: PropTypes.object,
    actions: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.elementType
    ]),
    backdropClose: PropTypes.bool,
    content: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.elementType
    ]),
    draggable: PropTypes.bool,
    footer: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.elementType
    ]),
    onBackdropClick: PropTypes.func,
    onCrossClick: PropTypes.func,
    onExit: PropTypes.func,
    title: PropTypes.node,
    withCross: PropTypes.bool
};
export const useLocalGenericDialog = ({ onExit, ...others } = {})=>{
    const [isOpen, close, open] = useModalState();
    const dialog = /*#__PURE__*/ React.createElement(GenericDialog, {
        open: isOpen,
        onExit: chainHandlersAsync([
            onExit,
            close
        ]),
        ...others
    });
    return [
        dialog,
        open,
        close
    ];
};
export default GenericDialog;
